/**
 * @class
 */
import Base from '@netx/core-asset/collections/assets';

import AssetModel from '@netx/core-asset/models/asset';
import AssetSetModel from '@netx/core-asset/models/assetSet';

import {types as assetSetTypes} from '@netx/core-asset/properties/assetSets';
import {pendingAssets} from '@netx/core-asset/collections/singleton/pendingAssets';
import {getPreference} from '@netx/core-user-next/utils/getPreference';

export default class CategoryAssets extends Base {
	/** @type {array} */
	get xhrTrackProgress() {
		return true;
	}

	/** @type {object} */
	get xhrLoadingMessages() {
		return {
			facetedSearch: 'Loading category assets',
			searchAssetBeanObjects: 'Loading category assets',
		};
	}

	/** @type {object} */
	get xhrLoadedMessages() {
		return {
			facetedSearch: 'Loaded category assets',
			searchAssetBeanObjects: 'Loaded category assets',
		};
	}

	/** @override */
	constructor(models, opts = {}) {
		super(models, opts);

		// Debounced methods
		this.refetch = _.debounce(this.refetch, 1000);

		/**
		 * @memberOf Netx.Collections.CategoryAssets
		 * @alias fetchArgs
		 * @type {object}
		 * @default
		 */
		this.fetchArgs = _.extend(
			{
				//remove: true
			},
			opts.fetchArgs,
		);

		/**
		 * Route of last category dislplayed
		 * @type {string}
		 */
		this.lastCategoryRoute = opts.lastCategoryRoute;

		this.selection.set([
			{
				elementType: 23, // search by category id
				elementSubType1: 2, // no recurse
			},
		]);

		/**
		 * @type {number}
		 * @default
		 */
		this.categoryId = opts.categoryId;
		this.selection.at(0).set({
			elementValue1: this.categoryId,
		});

		// Hey, listen!
		this
			// the old approach:
			.listenTo(
				dispatcher,
				'asset:pending:category:coming',
				this.checkPendingCategoryComingEvent,
			)
			.listenTo(
				dispatcher,
				'asset:pending:category:going',
				this.checkPendingCategoryGoingEvent,
			)
			// the new approach:
			.listenTo(
				dispatcher,
				'asset:pending:import:start',
				this.checkPendingImportEvent,
			)
			.listenTo(
				dispatcher,
				'asset:import:cancelled',
				this.checkCancelledImportEvent,
			)
			// TODO: maybe better to do this in the parser & get it done before sync.
			.listenTo(this, 'sync', this.checkPendingImportList)
			// Update our count when we hear about asset changes.
			.listenTo(dispatcher, 'asset:category:added', (assetId, catId, asset) => {
				if (this.categoryId !== catId) return;

				asset && this.add(asset);
				// triggers 'counted' on view
				this.pager.set({itemTotal: this.pager.get('itemTotal') + 1});
			})
			.listenTo(
				dispatcher,
				'asset:category:removed',
				(assetId, catId, asset) => {
					if (this.categoryId !== catId) return;

					let count = this.pager.get('itemTotal');
					if (count > 0) {
						count--;
						// triggers 'counted' on view
						this.pager.set({itemTotal: count});
					}
					this.remove(assetId);
				},
			)
			.listenTo(dispatcher, 'category:current:update', (data, page) => {
				//needed to reset pager between categories
				this.curPage = page;
			})
			// The above code references some code with TODO in the asset model
			// These events are from categorizing assets via the current cart
			// Update assets when changed
			.listenTo(dispatcher, 'asset:removed:from:category', (assetId, catId) => {
				if (this.categoryId !== catId) return;

				let count = this.pager.get('itemTotal');
				if (count > 0) {
					count--;
					// triggers 'counted' on view
					this.pager.set({itemTotal: count});
				}
				this.remove(assetId);
			})
			// No other way - currently
			.listenTo(
				dispatcher,
				'asset:added:to:category asset:moved:to:category',
				(assetId, catId) => {
					if (this.categoryId !== catId) return;
					this.refetch();
				},
			);
	}

	/**
	 * CategoryAssets listens for pending asset import events in its category, and automatically
	 * adds a pending asset when necessary.
	 */
	checkPendingImportEvent(upload, file, asset, catId) {
		// if the import is into our category ...
		if (catId != this.categoryId) return;
		this.checkPendingImportList();
	}

	/**
	 * If a pending import is cancelled, we need to remove the pending asset.
	 * @param {module:asset/models/asset} asset asset that was trying to import
	 */
	checkCancelledImportEvent(asset) {
		if (asset.get('categoryid') != this.categoryId) return;
		this.remove(asset);
	}

	/**
	 * After every fetch, CategoryAssets also checks the global pending assets list (Netx.asset.pending)
	 * for any pending assets that it hasn't yet incorporated, and adds pending assets for those.
	 */
	checkPendingImportList() {
		const _pendingAssets = pendingAssets.filterByCategory(this.categoryId);
		const assets = [];

		_.each(_pendingAssets, (pendingAsset) => {
			const id = pendingAsset.get('assetId');
			if (pendingAsset.get('direction') == 'coming') {
				// if we already have it, skip
				if (this.get(id)) return;
				assets.push(this.addPendingAsset(pendingAsset));
			} else if (pendingAsset.get('direction') == 'going') {
				// remove it if we have it.
				this.remove(id);
				// TODO: load another chunk?
			}
		});

		// Add to the beginning
		this.unshift(assets);
	}

	/**
	 * Add the asset represented by the pendingAsset model to our collection.
	 * @param {module:asset/models/pendingAsset} pendingAsset Pending asset
	 * @todo The `checkPendingImportList` method calls this and inserts the return into an array
	 * the `checkPendingCategoryComingEvent` method calls this (I think) in the hope of actually
	 * being added...
	 * Either fix or rename this method
	 */
	addPendingAsset(pendingAsset) {
		let pending = pendingAsset;

		if (!(pendingAsset instanceof AssetModel)) {
			pending = new AssetModel({assetId: pendingAsset.id});
			pending.fetch();
		}

		return pending;
	}

	/**
	 * If an asset has been added to this category on the DAM, add it here.
	 * @param {number} assetId Asset id
	 * @param {number} catId Category id
	 * @param {module:asset/models/pendingAsset} pendingAsset Pending asset
	 *
	 * NOTE: this is almost certainly added out of sort order.  Accurately-sorted results
	 * would come from triggering a fetch.  However, this is going to give users better feedback.
	 */
	checkPendingCategoryComingEvent(assetId, catId, pendingAsset) {
		if (catId != this.categoryId) return;

		// If we don't have it already,
		if (!this.get(assetId)) {
			// TODO: this really does nothing - see note on `addPendingAsset`
			// like this does not add this asset to anything but it will fetch it...
			this.addPendingAsset(pendingAsset);
		}
	}

	/**
	 * If an asset has been removed from this category on the DAM, remove it here
	 * @param {number} assetId Asset id
	 * @param {number} catId Category id
	 * @param {module:asset/models/pendingAsset} pendingAsset Pending asset
	 */
	checkPendingCategoryGoingEvent(assetId, catId, pendingAsset) {
		if (catId != this.categoryId) return;

		this.remove(this.get(assetId));
	}

	/**
	 * @method
	 * @param {number} catId Category id
	 */
	setCategoryId(catId) {
		dispatcher.off(
			'category:edit:success' + this.categoryId,
			this.refetch,
			this,
		);
		this.categoryId = catId;
		dispatcher.on(
			'category:edit:success' + this.categoryId,
			this.refetch,
			this,
		);
	}

	/**
	 * Re-get the current chunk of the current collection
	 * @method
	 * @param {object} opts Request options
	 */
	refetch(opts) {
		opts = opts || {};
		this.fetch(_.extend({duplicateQueryPolicy: 'abort'}, opts));
	}

	/**
	 * @method
	 * @param {number|string} catId Category id
	 */
	getCategoryAssets(catId) {
		// TODO: remove route-mgt code from models/collections
		this.routeContext = 'category/' + catId;

		const selection = this.selection;
		// reset facets only if on new category
		if (selection.searchFacets && catId != this.categoryId) {
			selection.searchFacets.reset();
		}

		// Ensure number
		catId = parseInt(catId);
		// Set selection
		const sortField = getPreference('asset.list.sort.field');
		const sortOrder = getPreference('asset.list.sort.order');
		selection.sortField = sortField == null ? 'fileName' : sortField;
		selection.sortOrder = sortOrder == null ? 0 : sortOrder;
		selection.at(0).set({elementValue1: catId});
		this.setCategoryId(catId);

		// Get assets
		return this.fetch({
			duplicateQueryPolicy: 'abort',
			cacheSelection: true,
		}).then(
			() => {
				// Success
			},
			(err) => {
				console.warn('getCategoryAssets err: %o', err);
			},
		);
	}

	/**
	 * return an AssetSet object defining the set of this category's assets.
	 */
	toAssetSet() {
		return new AssetSetModel({
			type: assetSetTypes.category,
			ids: [this.categoryId],
			label: i18n.t('i18n.category'),
		});
	}

	/** @type {string} */
	static get __name__() {
		return 'CategoryAssets';
	}
}
